<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="show"
      max-width="600"
      @input="v => v || closeDialog()"
    >
      <v-card>
        <v-card-title :class="colorTheme">
          <span class="headline white--text text-uppercase">Upload and Assign File - {{ instrument }}</span>
        </v-card-title>
        <v-alert
          v-if="error"
          type="error"
          :value="true"
          class="mt-2"
        >
          {{ error }}
        </v-alert>
        <v-card-text>
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
          >
            <v-container fluid>
              <v-layout
                row
                wrap
              >
                <v-flex
                  xs12
                  sm6
                >
                  <v-select
                    v-model="category"
                    :color="colorTheme"
                    :items="fileCategories"
                    item-text="name"
                    item-value="value"
                    label="Category"
                    :rules="requiredRule"
                    @change="onCategoryChange()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  sm6
                >
                  <v-text-field
                    v-model="name"
                    label="Name"
                    :color="colorTheme"
                    :rules="requiredRule"
                  />
                </v-flex>
              </v-layout>
              <v-layout
                row
                wrap
              >
                <v-flex
                  xs12
                  sm6
                >
                  <v-text-field
                    v-model="title"
                    label="Template Name"
                    :color="colorTheme"
                  />
                </v-flex>
                <v-flex
                  xs12
                  sm6
                >
                  <v-select
                    v-model="usage"
                    :color="colorTheme"
                    multiple
                    :items="fileUsages"
                    item-text="name"
                    item-value="value"
                    label="Usage"
                  />
                </v-flex>
              </v-layout>
              <v-layout
                row
                wrap
              >
                <v-flex
                  xs12
                  sm6
                >
                  <v-menu
                    :color="colorTheme"
                    :close-on-content-click="true"
                    full-width
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="fileName"
                        readonly
                        :color="colorTheme"
                        label="Attach document"
                        prepend-icon="attach_file"
                        :rules="requiredRule"
                        v-on="on"
                        @click="$refs.fileInput.click()"
                      />
                    </template>
                    <input
                      ref="fileInput"
                      :color="colorTheme"
                      type="file"
                      accept="application/pdf"
                      style="display:none"
                      @input="onFileChange"
                    >
                  </v-menu>
                </v-flex>
                <v-flex
                  xs12
                  sm6
                >
                  <v-checkbox
                    v-model="template"
                    label="Template"
                    :color="colorTheme"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :color="colorTheme"
            flat="flat"
            :disabled="processing"
            @click="closeDialog()"
          >
            cancel
          </v-btn>

          <v-btn
            :color="colorTheme"
            flat="flat"
            :disabled="processing"
            @click="submit()"
          >
            create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { mapActions } from 'vuex';
import FileCategoriesEnum, {
  FileCategoriesForBondFullEnum,
  FileCategoriesForBondPriipEnum,
  FileCategoriesForBondWibEnum,
  FileCategoriesForEcspEnum,
} from '../../enum/financialProducts/FileCategories';
import FileUsagesEnum from '../../enum/financialProducts/FileUsages';
import { FinancialInstrumentsEnum } from '@/enum/financialProducts/FinancialInstruments';

export default {
  name: 'UploadAndAssignFile',
  props: {
    dialog: {
      type: Boolean,
      default: false,
      required: true,
    },
    processing: {
      type: Boolean,
      default: false,
    },
    instrument: {
      type: String,
      required: true,
    },
    submitAction: {
      type: Function,
      required: true,
      default: () => ({}),
    },
    cancelAction: {
      type: Function,
      required: true,
      default: () => ({}),
    },
    setUsages: {
      type: Function,
      required: true,
      default: () => ({}),
    },
    colorTheme: {
      type: String,
      default: 'blue',
    },
  },
  data() {
    return {
      valid: true,
      show: this.dialog,
      category: null,
      title: null,
      file: null,
      name: null,
      usage: null,
      error: '',
      template: false,
      requiredRule: [
        v => !!v || 'field is required',
      ],
    };
  },
  computed: {
    fileCategories() {
      switch (this.instrument) {
        case FinancialInstrumentsEnum.BOND_WIB:
          return Object.values(FileCategoriesForBondWibEnum).sort();
        case FinancialInstrumentsEnum.BOND_FULL:
          return Object.values(FileCategoriesForBondFullEnum).sort();
        case FinancialInstrumentsEnum.BOND_PRIIP:
          return Object.values(FileCategoriesForBondPriipEnum).sort();
        case FinancialInstrumentsEnum.LOAN_ECSP:
          return Object.values(FileCategoriesForEcspEnum).sort();
        default:
          return Object.values(FileCategoriesEnum).sort();
      }
    },
    fileName() {
      return this.file ? this.file.name : null;
    },
    fileUsages() {
      return Object.keys(FileUsagesEnum).map(key => FileUsagesEnum[key]);
    },
  },
  watch: {
    dialog(val) {
      this.show = val;
    },
  },
  methods: {
    ...mapActions(['uploadFile']),
    async submit() {
      if (this.$refs.form.validate()) {
        try {
          const uploadParams = {
            category: this.category,
            title: this.title,
            fileName: this.fileName,
            file: this.file,
          };
          if (this.template) {
            uploadParams.template = true;
          }
          await this.uploadFile(uploadParams);
          if (!this.usage) {
            this.submitAction({
              category: this.category,
              fileName: this.fileName,
              name: this.name,
              usage: this.usage,
            });
          } else {
            Object.keys(this.usage).forEach((item) => {
              this.submitAction({
                category: this.category,
                fileName: this.fileName,
                name: this.name,
                usage: this.usage[item],
              });
            });
          }
          this.closeDialog();
        } catch (err) {
          // eslint-disable-next-line no-console
          this.error = err.response.data.error;
        }
      }
    },
    closeDialog() {
      this.cancelAction();
      this.setToDefault();
    },
    setToDefault() {
      this.category = null;
      this.title = null;
      this.file = null;
      this.valid = true;
      this.name = null;
      this.usage = null;
      this.template = false;
      this.error = '';
    },
    async onFileChange(e) {
      try {
        const [file] = e.target.files;
        this.file = file;
      } catch (err) {
        console.error(err);
      }
    },
    onCategoryChange() {
      this.usage = this.setUsages(this.category);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/financialProducts/UploadAndAssignFile.vue